import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { HeroBookingWidget } from "@/components/HeroBookingWidget";
import { useLanguage } from "@/context/LanguageContext";
import { useSeo } from "@/lib/useSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import { IMAGES } from "@/data/images";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PlaneTakeoff, UserCheck, Clock, Luggage, Tag, Baby, ArrowRight, Phone } from "lucide-react";

const included = [
  { icon: PlaneTakeoff, key: "flight" },
  { icon: UserCheck, key: "greet" },
  { icon: Clock, key: "wait" },
  { icon: Luggage, key: "luggage" },
  { icon: Tag, key: "fixed" },
  { icon: Baby, key: "child" },
];

export default function AirportTransfers() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  useSeo({ ...(PAGE_SEO.airport[lang] || PAGE_SEO.airport.en), path: PAGE_SEO.airport.path, image: PAGE_SEO.airport.image, lang });
  const phone = t("hero.phone");

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[92vh] flex items-center">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.privateDriver} alt="Executive airport transfer chauffeur SUV in Montréal" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-16 w-full grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs tracking-luxe uppercase text-white/60 mb-5">{t("air.eyebrow")}</p>
            <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">{t("air.title")}</h1>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mt-6 leading-relaxed">{t("air.subtitle")}</p>
            <a href={`tel:+1${phone.replace(/[^0-9]/g, "")}`} className="inline-block mt-8">
              <Button variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 h-12 px-7">
                <Phone className="h-4 w-4 mr-2" /> {t("hero.call")} · {phone}
              </Button>
            </a>
          </div>
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
            <HeroBookingWidget />
          </motion.div>
        </div>
      </section>

      {/* Included */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-14 max-w-2xl leading-tight">{t("air.inc.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {included.map((f) => (
            <div key={f.key} className="rounded-xl border border-white/10 bg-card p-7" data-testid={`air-feature-${f.key}`}>
              <div className="h-11 w-11 rounded-full border border-white/20 flex items-center justify-center mb-5">
                <f.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-white text-lg font-medium mb-2">{t(`air.inc.${f.key}.t`)}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{t(`air.inc.${f.key}.d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-white/10 bg-card/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <h2 className="font-serif font-light text-3xl sm:text-4xl text-white mb-12 text-center">{t("air.how.title")}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[1, 2, 3].map((n) => (
              <div key={n} className="text-center" data-testid={`air-step-${n}`}>
                <div className="mx-auto h-12 w-12 rounded-full border border-white/20 flex items-center justify-center mb-5 font-serif text-xl text-white">{n}</div>
                <h3 className="text-white text-lg font-medium mb-2">{t(`air.how.s${n}.t`)}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-xs mx-auto">{t(`air.how.s${n}.d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl text-white mb-12 text-center leading-tight">{t("air.faq.title")}</h2>
        <Accordion type="single" collapsible className="w-full">
          {[1, 2, 3, 4].map((n) => (
            <AccordionItem key={n} value={`q${n}`} className="border-white/10">
              <AccordionTrigger className="text-white text-left hover:no-underline text-base">{t(`air.faq.q${n}`)}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{t(`air.faq.a${n}`)}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="relative py-28">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.cityNight} alt="Montréal at night" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/75" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <h2 className="font-serif font-light text-4xl sm:text-5xl text-white leading-tight">{t("air.cta.title")}</h2>
          <p className="text-white/70 mt-5 text-lg">{t("air.cta.sub")}</p>
          <Button data-testid="air-cta-btn" className="rounded-full bg-white text-black hover:bg-white/85 h-14 px-10 text-base mt-9"
            onClick={() => navigate("/book?service=private_driver")}>
            {t("widget.submit")} <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
